import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Spin, Card, Switch, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faList, faPlus, faPen, faTrash, faWifi, faServer } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import {
	useLazyGetPermissionsByRoleQuery,
	usePostAssignPermissionMutation,
	useLazyGetRoleQuery,
} from "../../../redux/permissions/permissionsAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";
import {
	PermissionInterface,
	CustomPermissionInterface,
	GroupedPermissionsInterface,
	GroupedChildInterface,
} from "../../../interfaces/Permissions.interface";
import { RoleInterface, RolePermissionsInterface } from "../../../interfaces/Roles.interface";

function RolesAndPermissionsTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { theme } = useSelector((state: RootState) => state.home);
	const location = useLocation();
	const idRole = new URLSearchParams(location.search).get("idRole");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [permissions, setPermissions] = useState<GroupedPermissionsInterface[]>([]);
	const [role, setRole] = useState<RoleInterface>({
		id: "",
		name: "",
		active: true,
		org_id: "",
		RolePermissions: [
			{
				PermissionLevelOffline: false,
				PermissionLevelOnline: false,
				id: "",
				permission_id: "",
				role_id: "",
			},
		],
		orgs: {
			id: "",
			name: "",
			type: "",
			parent_id: "",
			country: "",
			state: "",
			city: "",
			address: "",
			phone: "",
			phone_code: "",
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
			deleted_at: null,
		},
		menu: {},
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { userLogged } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetPermissions,
		{ data: dataGetPermissions, isLoading: isLoadingGetPermissions, isFetching: isFetchingGetPermissions },
	] = useLazyGetPermissionsByRoleQuery();

	const [triggerGetRole, { data: dataGetRole, isLoading: isLoadingGetRole, isFetching: isFetchingGetRole }] =
		useLazyGetRoleQuery();

	const [
		triggerPostAssignPermission,
		{
			isSuccess: isSuccessPostAssignPermission,
			isLoading: isLoadingPostAssignPermission,
			isError: isErrorPostAssignPermission,
			error: errorPostAssignPermission,
			// reset: resetPostAssignPermission,
		},
	] = usePostAssignPermissionMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getDataRole = async () => {
		const token = await GETJwtToken();
		triggerGetRole({
			token,
			id: idRole,
		});
	};

	const getDataPermissions = async (id: string) => {
		const token = await GETJwtToken();
		triggerGetPermissions({
			token,
			id,
		});
	};

	const customCardHeight = (maxLength: number) => {
		if (maxLength >= 0 && maxLength <= 5) {
			return 225;
		}
		if (maxLength >= 6 && maxLength <= 18) {
			return maxLength * 35 + 30;
		}
		return 250;
	};

	function groupPermissions(data_: PermissionInterface[], role_: RoleInterface) {
		const grouped: GroupedPermissionsInterface[] = [];
		const rolePermissions: RolePermissionsInterface[] = role_?.RolePermissions ?? [];

		data_.forEach((permission: PermissionInterface) => {
			const { parent, group } = permission;

			let parentGroup = grouped.find((pg: GroupedPermissionsInterface) => pg.parent === parent);
			if (!parentGroup) {
				parentGroup = { parent, groups: [], maxLength: 0 };
				grouped.push(parentGroup);
			}

			let groupObj = parentGroup.groups.find((g: GroupedChildInterface) => g.group === group);
			if (!groupObj) {
				groupObj = { group, childs: [] };
				parentGroup.groups.push(groupObj);
			}

			if (rolePermissions.length > 0) {
				const index = rolePermissions.findIndex(
					(item: RolePermissionsInterface) => permission.id === item.permission_id
				);
				if (index >= 0) {
					groupObj.childs.push({
						...permission,
						online: rolePermissions[index].PermissionLevelOnline,
						offline: rolePermissions[index].PermissionLevelOffline,
					});
				} else {
					groupObj.childs.push({ ...permission, online: false, offline: false });
				}
			} else {
				groupObj.childs.push({ ...permission, online: false, offline: false });
			}
		});

		grouped.forEach((pg: GroupedPermissionsInterface) => {
			pg.groups.sort((a: GroupedChildInterface, b: GroupedChildInterface) => a.group.localeCompare(b.group));
			// eslint-disable-next-line no-param-reassign
			pg.maxLength = customCardHeight(Math.max(...pg.groups.map((g: GroupedChildInterface) => g.childs.length)));
		});

		setPermissions(grouped);
	}

	const permissionNameIcon = (name: string) => {
		if (name === "GET") {
			return <FontAwesomeIcon icon={faList} className='generalStyles__info generalStyles__mrFix' />;
		}
		if (name === "POST") {
			return <FontAwesomeIcon icon={faPlus} className='generalStyles__info generalStyles__mrFix' />;
		}
		if (name === "PUT") {
			return <FontAwesomeIcon icon={faPen} className='generalStyles__info generalStyles__mrFix' />;
		}
		if (name === "DELETE") {
			return <FontAwesomeIcon icon={faTrash} className='generalStyles__info generalStyles__mrFix' />;
		}
		return <FontAwesomeIcon icon={faUserShield} className='generalStyles__info generalStyles__mrFix' />;
	};

	const onChangePermission = async (online: boolean, offline: boolean, child: CustomPermissionInterface) => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: {
				roleId: idRole,
				permissionId: child.id,
				permission_level_online: online,
				permission_level_offline: offline,
			},
		};
		triggerPostAssignPermission(BODY);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (userLogged && userLogged.role && userLogged.role.id) {
			getDataPermissions(userLogged.role.id);
		}
	}, [userLogged]);

	useEffect(() => {
		if (dataGetPermissions && dataGetPermissions.data) {
			getDataRole();
		}
	}, [dataGetPermissions]);

	useEffect(() => {
		if (dataGetRole && dataGetRole.data) {
			setRole(dataGetRole.data);
			groupPermissions(dataGetPermissions.data, dataGetRole.data);
		}
	}, [dataGetRole]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<Spin
				spinning={
					isLoadingGetPermissions ||
					isLoadingGetRole ||
					isLoadingPostAssignPermission ||
					isFetchingGetPermissions ||
					isFetchingGetRole
				}
			>
				{permissions.map((parent: GroupedPermissionsInterface) => (
					<div key={parent.parent} className={`layout__contentPage__${theme} mt-3 `}>
						<div style={{ marginLeft: "25px" }}>
							<h3>{parent.parent}</h3>
							<div className='generalStyles__flex' style={{ marginTop: "-15px" }}>
								<p className='generalStyles__info'>{role.orgs.name}</p>
								<p style={{ marginLeft: "10px" }}>{role.name}</p>
							</div>
						</div>
						<div className='generalStyles__pageHeader generalStyles__flex'>
							{parent.groups.map((group: GroupedChildInterface) => (
								<div key={group.group}>
									<Card
										loading={
											isLoadingGetPermissions ||
											isLoadingGetRole ||
											isLoadingPostAssignPermission ||
											isFetchingGetPermissions ||
											isFetchingGetRole
										}
										type='inner'
										title={group.group}
										style={{ width: 335, height: parent.maxLength, margin: "5px" }}
										bodyStyle={{ padding: 15 }}
										extra={
											<>
												{false && (
													<div className='generalStyles__flex' style={{ marginRight: "-13px" }}>
														<div>
															<FontAwesomeIcon icon={faWifi} className='generalStyles__info generalStyles__mrFix' />
														</div>
														<div>
															<Tooltip placement='top' title='Select all online'>
																<Switch size='small' />
															</Tooltip>
														</div>
														<div style={{ marginLeft: "15px" }}>
															<FontAwesomeIcon icon={faServer} className='generalStyles__info generalStyles__mrFix' />
														</div>
														<div>
															<Tooltip placement='top' title='Select all offline'>
																<Switch size='small' />
															</Tooltip>{" "}
														</div>
													</div>
												)}
											</>
										}
									>
										{group.childs.map((child: CustomPermissionInterface) => (
											<div key={child.id} className='generalStyles__spaceBetween mt-2'>
												<div className='generalStyles__flex'>
													<div style={{ width: "25px" }}>{permissionNameIcon(child.method)}</div>
													<div className='generalStyles__ellipsis__150px'>
														<Tooltip placement='top' title={child.name}>
															{child.name}
														</Tooltip>
													</div>
												</div>
												<div className='generalStyles__flex'>
													<div>
														<FontAwesomeIcon icon={faWifi} className='generalStyles__info generalStyles__mrFix' />
													</div>
													<div>
														<Tooltip placement='top' title='Online'>
															<Switch
																checked={child.online}
																size='small'
																onClick={(checked: boolean) => {
																	onChangePermission(checked, child.offline, child);
																}}
															/>
														</Tooltip>
													</div>
													<div style={{ marginLeft: "15px" }}>
														<FontAwesomeIcon icon={faServer} className='generalStyles__info generalStyles__mrFix' />
													</div>
													<div>
														<Tooltip placement='top' title='Offline'>
															<Switch
																checked={child.offline}
																size='small'
																onClick={(checked: boolean) => {
																	onChangePermission(child.online, checked, child);
																}}
															/>
														</Tooltip>{" "}
													</div>
												</div>
											</div>
										))}
									</Card>
								</div>
							))}
						</div>
					</div>
				))}
			</Spin>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostAssignPermission}
				isSuccess={isSuccessPostAssignPermission}
				requestType='PUT'
				error={errorPostAssignPermission}
				name='Permission'
			/>
		</div>
	);
}

export default RolesAndPermissionsTable;
